import React, { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Chip, InputBase } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import cn from 'classnames';

const useStyles = makeStyles(theme => ({
    root: {
        // Min width same as height, so it can't get overly vertically thin
        minWidth: 32,

        '&:focus-within': {
            cursor: 'text',
        },
    },

    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    input: {
        // width: '100%',
        font: 'inherit',
        color: 'inherit',

        '&:not($inputDisabled) :not(:focus)': {
            cursor: 'pointer',
        },
    },
    inputDisabled: {},
    inputSizer: {
        opacity: 0,
        maxHeight: 0,
    },
}));

/**
 * @typedef Props
 * @property {import('@mui/material').InputBaseProps['value']} value
 * @property {import('@mui/material').InputBaseProps['onChange']} onChange
 * @property {import('@mui/material').InputBaseProps} [InputProps]
 */

/**
 * @param {Props & import('@mui/material').ChipProps} props
 */
export default forwardRef(function EditableChip(props, ref) {
    const {
        value,
        onChange,
        disabled,
        disabledColor = 'default',
        InputProps: {
            style: inputStyle,
            ...InputProps
        } = {},
        className: propsClass,
        ...other
    } = props;

    const classes = useStyles(other);

    /** @type {React.MutableRefObject<HTMLInputElement>} */
    const inputRef = useRef();

    /** @type {React.MutableRefObject<HTMLDivElement} */
    const sizerRef = useRef();

    const focusField = useCallback(
        /** @param {PointerEvent} event */
        event => {
            inputRef.current.focus();

            event.stopPropagation();
            event.preventDefault();
        }, []
    );

    const [inputWidth, setInputWidth] = useState(0);

    useLayoutEffect(() => {
        const { width } = sizerRef.current.getBoundingClientRect();

        setInputWidth(width);
    }, [value]);

    return (
        <Chip
            ref={ref}
            className={cn(classes.root, propsClass)}
            onClick={focusField}
            /* Exclude the chip itself from tab navigation, allowing the user
             *  to directly tab through the input elements. */
            tabIndex={-1}
            label={(
                <div className={classes.inputContainer}>
                    {/* A 'sizer' element is used to derive the width of the
                      *     text in the field, which is then used to set the
                      *     input's width.
                      */}
                    <div className={classes.inputSizer} ref={sizerRef}>
                        {value}
                    </div>

                    <InputBase
                        className={cn(classes.input, disabled && classes.inputDisabled)}
                        inputRef={inputRef}
                        value={value}
                        onChange={onChange}
                        readOnly={disabled}
                        style={{ ...inputStyle, width: inputWidth }}
                        {...InputProps}
                    />
                </div>
            )}
            {...other}
            {...(disabled && {
                ...(!!disabledColor && { color: disabledColor }),
                onDelete: undefined,
                onClick: undefined,
            })}
        />
    );
});